import React, { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";

export default function PromotionalPopup({
  icon,
  open,
  title,
  message,
  badge = "Limited Time",
  subtitle = "Promotional Offer",
  backgroundImageUrl,
  endsAtLabel,
  actionLabel = "View",
  onAction,
  onClose,
}) {
  // 👇 NEW: internal state to handle fade-out
  const [visible, setVisible] = useState(open);

  // 👇 NEW: whenever `open` changes, show modal
  useEffect(() => {
    if (open) setVisible(true);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // 👇 NEW: Auto-close with fade-out
  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => {
      // start fade-out
      setVisible(false);

      // actually close after animation duration
      setTimeout(() => {
        onClose?.();
      }, 300); // 👈 match fade-out duration
    }, 5000); // 👈 auto-close after 5s

    return () => clearTimeout(timer);
  }, [open, onClose]);

  // 👇 NEW: unified close function
  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  if (!open && !visible) return null; // hide completely after fade-out

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 transition-opacity duration-300 ${
        visible ? "opacity-100" : "opacity-0"
      }`} // 👈 NEW: fade-in/out
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full max-w-sm rounded-3xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.4)] overflow-hidden transition-transform duration-300 ${
          visible ? "scale-100" : "scale-95"
        }`} // 👈 NEW: slight zoom-out for fade effect
      >
        {/* Top Gradient Area */}
        <div className="bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500 p-6 text-center text-white relative">
          {/* Close */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 bg-white/20 hover:bg-white/30 rounded-full w-8 h-8 flex items-center justify-center"
          >
            <FaTimes />
          </button>

          {/* Icon / Image */}
          <div className="flex justify-center mb-4">
            {icon ? (
              <div className="text-4xl">{icon}</div>
            ) : (
              <img
                src={backgroundImageUrl}
                alt="popup"
                className="w-20 h-20 object-contain"
              />
            )}
          </div>

          {/* Title */}
          <h2 className="text-xl font-extrabold">{title}</h2>

          {/* Subtitle */}
          {subtitle && <p className="text-sm opacity-90 mt-1">{subtitle}</p>}
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          {/* Description */}
          {message && (
            <p className="text-sm text-slate-600 leading-relaxed">{message}</p>
          )}

          {/* Button */}
          <button
            onClick={() => onAction?.()}
            className="mt-6 w-full rounded-xl bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 px-5 py-3 text-sm font-semibold text-white shadow-lg hover:scale-[1.03] transition-all"
          >
            {actionLabel || "Explore Now 🚀"}
          </button>

          {/* Optional secondary button */}
          <button
            onClick={handleClose}
            className="mt-3 text-sm text-slate-500 hover:text-slate-700"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}